import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isEqual } from "lodash";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import {
  type ContractExtensionDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import {
  ExtensionFields,
  type ExtensionDraft,
} from "../../../../../common/extension/ExtensionFields";
import {
  DEFAULT_EXTENSION,
  extensionSeededWithin,
  isDeadlineWithinContract,
  isWholeExtensionNumber,
} from "../../../../../common/extension/extensionRules";
import { offersExtensions } from "../../../../../common/procurement/procedure";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import {
  baseContractMonths,
  contractMonthsBeforeEachExtension,
  lengthWithExtensions,
} from "../utils/extensionLength";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { FieldSaveError } from "./FieldSaveError";
import { useBlockStepLeave } from "./stepLeaveBlockers";

const MAX_EXTENSIONS = 20;

interface ExtensionRow extends ExtensionDraft {
  id: string;
}

const toRow = (extension: ContractExtensionDto): ExtensionRow => ({
  id: uuid(),
  ...extension,
});

const toExtensionValues = ({
  duration,
  unit,
  exerciseDeadlineMonths,
}: ContractExtensionDto): ContractExtensionDto => ({
  duration,
  unit,
  exerciseDeadlineMonths,
});

const rowsFor = (
  savedExtensions: ContractExtensionDto[],
  noExtensionSaved: boolean,
  contractMonths: number | null,
): ExtensionRow[] =>
  noExtensionSaved
    ? [toRow(extensionSeededWithin(contractMonths))]
    : savedExtensions.map(toRow);

const toExtension = (
  { duration, unit, exerciseDeadlineMonths }: ExtensionRow,
  contractMonthsBefore: number | null,
): ContractExtensionDto | null =>
  isWholeExtensionNumber(duration) &&
  isWholeExtensionNumber(exerciseDeadlineMonths) &&
  isDeadlineWithinContract(exerciseDeadlineMonths, contractMonthsBefore)
    ? { duration, unit, exerciseDeadlineMonths }
    : null;

const validExtensions = (
  rows: ExtensionRow[],
  contractMonths: number | null,
): ContractExtensionDto[] | null => {
  const monthsBefore = contractMonthsBeforeEachExtension(contractMonths, rows);
  const extensions = rows.map((row, index) =>
    toExtension(row, monthsBefore[index]),
  );
  return extensions.every((e) => e != null)
    ? (extensions as ContractExtensionDto[])
    : null;
};

interface Props {
  eventId: string;
}

export const ContractOptionsField = ({ eventId }: Props) => {
  const { state } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const procedureType =
    state.eventContext.type === "Public"
      ? state.eventContext.procedureType
      : null;
  if (!offersExtensions(procedureType, state.eventData.contractDuration)) return null;
  return (
    <>
      <Divider my="4" />
      <ExtensionOptions eventId={eventId} />
    </>
  );
};

const ExtensionOptions = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    SourcingEventDto,
    SourcingEventPatchDto
  >(eventId);
  const { show: confirm, modal: confirmationModal } = useConfirmationModal();

  const { readinessFor } = useReadinessByField(eventId);
  const formatFieldError = useFieldErrorTranslation();

  const options = state.eventData.options;
  const hasOptions = options?.hasOptions ?? false;
  const earlierText = (options?.descriptions ?? []).filter(
    (d) => d.trim() !== "",
  );
  const contractMonths = baseContractMonths(state.eventData.contractDuration);
  const savedExtensions = useMemo(
    () => (options?.extensions ?? []).map(toExtensionValues),
    [options?.extensions],
  );
  const noExtensionSaved = hasOptions && savedExtensions.length === 0;
  const [rows, setRows] = useState<ExtensionRow[]>(() =>
    rowsFor(savedExtensions, noExtensionSaved, contractMonths),
  );
  const hasUncommittedNumbers = useRef(noExtensionSaved);
  const optionsWithoutExtensionsError = hasOptions
    ? readinessFor("ExtensionsPresentWithOptions")?.error
    : undefined;

  const saving = fieldStatus("eventData.options");
  const isSaving = saving === "saving" || saving === "pending";

  const saveRows = useCallback(
    (next: ExtensionRow[]) => {
      const extensions = validExtensions(next, contractMonths);
      if (extensions == null) return;
      hasUncommittedNumbers.current = false;
      updateField({ eventData: { options: { hasOptions: true, extensions } } });
    },
    [updateField, contractMonths],
  );

  const followedExtensions = useRef(savedExtensions);
  useEffect(() => {
    const savedElsewhere =
      !isEqual(followedExtensions.current, savedExtensions) &&
      !isEqual(validExtensions(rows, contractMonths), savedExtensions);
    const optionsTurnedOnElsewhere = hasOptions && rows.length === 0;
    followedExtensions.current = savedExtensions;
    if (!savedElsewhere && !optionsTurnedOnElsewhere) return;
    hasUncommittedNumbers.current = noExtensionSaved;
    setRows(rowsFor(savedExtensions, noExtensionSaved, contractMonths));
  }, [savedExtensions, hasOptions, noExtensionSaved, rows, contractMonths]);

  const contractMonthsRowsWereCheckedAgainst = useRef<
    number | null | undefined
  >(earlierText.length > 0 ? contractMonths : undefined);
  useEffect(() => {
    if (contractMonthsRowsWereCheckedAgainst.current === contractMonths) return;
    contractMonthsRowsWereCheckedAgainst.current = contractMonths;
    if (hasOptions && rows.length > 0 && hasUncommittedNumbers.current)
      saveRows(rows);
  }, [contractMonths, hasOptions, rows, saveRows]);

  const changeRows = (next: ExtensionRow[]) => {
    setRows(next);
    saveRows(next);
  };

  const commitNumbers = () => {
    if (hasUncommittedNumbers.current) saveRows(rows);
  };

  const handleToggle = (enabled: boolean) => {
    if (!enabled) {
      setRows([]);
      updateField({
        eventData: { options: { hasOptions: false, extensions: [] } },
      });
      return;
    }
    const next =
      rows.length > 0 ? rows : [toRow(extensionSeededWithin(contractMonths))];
    setRows(next);
    const extensions = validExtensions(next, contractMonths);
    hasUncommittedNumbers.current = extensions == null;
    updateField({
      eventData: {
        options:
          extensions == null
            ? { hasOptions: true }
            : { hasOptions: true, extensions },
      },
    });
  };

  const addRow = () => {
    const monthsBeforeNewRow = contractMonthsBeforeEachExtension(
      contractMonths,
      [...rows, DEFAULT_EXTENSION],
    ).at(-1);
    changeRows([
      ...rows,
      toRow(extensionSeededWithin(monthsBeforeNewRow ?? null)),
    ]);
  };

  const updateRow = (id: string, change: Partial<ExtensionRow>) => {
    const next = rows.map((row) =>
      row.id === id ? { ...row, ...change } : row,
    );
    if ("unit" in change) {
      changeRows(next);
      return;
    }
    hasUncommittedNumbers.current = true;
    setRows(next);
  };

  const removeEarlierText = (text: string) =>
    confirm({
      title: String(t("Remove option text from before?")),
      description: t("The text cannot be restored."),
      confirmButtonText: String(t("Remove")),
      confirmButtonColor: "red",
      onConfirmed: () => {
        const remaining = options?.descriptions ?? [];
        const at = remaining.indexOf(text);
        if (at === -1) return;
        updateField({
          eventData: {
            options: { descriptions: remaining.filter((_, i) => i !== at) },
          },
        });
      },
    });

  const shownExtensions = hasOptions
    ? validExtensions(rows, contractMonths)
    : [];
  const rowsAreValid = shownExtensions != null;
  const length = lengthWithExtensions(contractMonths, shownExtensions ?? []);
  const rowsBox = useRef<HTMLDivElement>(null);
  useBlockStepLeave("contract-extensions", !rowsAreValid, () => {
    const firstInvalid = rowsBox.current?.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );
    (firstInvalid ?? rowsBox.current)?.focus();
    (firstInvalid ?? rowsBox.current)?.scrollIntoView({ block: "center" });
  });
  const monthsBeforeEachRow = contractMonthsBeforeEachExtension(
    contractMonths,
    rows,
  );
  const unitLabel =
    length?.unit === "year"
      ? t("year", { count: length.total })
      : t("month", { count: length?.total ?? 0 });

  return (
    <FormControl>
      <Flex
        align="center"
        justify="space-between"
        gap="4"
        mb={hasOptions ? "4" : "0"}
      >
        <FormLabel mb="0">{t("Contract options")}</FormLabel>
        <Flex align="center" gap="2">
          {isSaving && <Spinner size="xs" />}
          <ButtonGroup isAttached size="sm">
            <Button
              colorScheme={hasOptions ? "teal" : "gray"}
              variant={hasOptions ? "solid" : "outline"}
              onClick={() => handleToggle(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              colorScheme={!hasOptions ? "teal" : "gray"}
              variant={!hasOptions ? "solid" : "outline"}
              onClick={() => handleToggle(false)}
            >
              {t("No")}
            </Button>
          </ButtonGroup>
        </Flex>
      </Flex>
      {optionsWithoutExtensionsError && (
        <Text
          fontSize="xs"
          color="smError"
          mb="4"
          data-testid="options-without-extensions"
        >
          {formatFieldError(optionsWithoutExtensionsError)}
        </Text>
      )}
      <FieldSaveError eventId={eventId} path="eventData.options" />
      {confirmationModal}

      {hasOptions && (
        <Stack spacing="4">
          {earlierText.length > 0 && (
            <Box
              borderWidth="1px"
              borderColor="smWarningSoftBorder"
              bg="smWarningSoft"
              borderRadius="md"
              p="3"
            >
              <Text fontSize="sm" fontWeight="semibold">
                {t("Option text from before")}
              </Text>
              <Text fontSize="sm" color="smTextSecondary" mb="2">
                {t(
                  "This text is not sent in the notice. Register the extensions below, and remove the text once they cover it.",
                )}
              </Text>
              <Stack spacing="1">
                {earlierText.map((text, index) => (
                  <Flex
                    key={`${index}-${text}`}
                    align="center"
                    justify="space-between"
                    gap="2"
                  >
                    <Text fontSize="sm">{text}</Text>
                    <IconButton
                      aria-label={t("Remove option text from before")}
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      isDisabled={isSaving}
                      onClick={() => removeEarlierText(text)}
                    />
                  </Flex>
                ))}
              </Stack>
            </Box>
          )}

          <Stack spacing="3" ref={rowsBox} tabIndex={-1}>
            {rows.map((row, index) => (
              <Box
                key={row.id}
                borderWidth="1px"
                borderColor="smBorder"
                borderRadius="md"
                p="3"
              >
                <Flex align="center" justify="space-between" mb="2">
                  <Text fontSize="sm" fontWeight="semibold">
                    {t("Extension {{number}}", { number: index + 1 })}
                  </Text>
                  {rows.length > 1 && (
                    <IconButton
                      aria-label={t("Remove extension {{number}}", {
                        number: index + 1,
                      })}
                      icon={<FaTrash />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() =>
                        changeRows(rows.filter((r) => r.id !== row.id))
                      }
                    />
                  )}
                </Flex>
                <ExtensionFields
                  value={row}
                  extensionNumber={index + 1}
                  contractMonthsBefore={monthsBeforeEachRow[index]}
                  onChange={(change) => updateRow(row.id, change)}
                  onNumberBlur={commitNumbers}
                />
              </Box>
            ))}
          </Stack>
          {rows.length < MAX_EXTENSIONS && (
            <Box>
              <Button
                variant="ghost"
                colorScheme="teal"
                size="sm"
                onClick={addRow}
              >
                + {t("Add extension")}
              </Button>
            </Box>
          )}

          {!rowsAreValid && (
            <Text
              fontSize="sm"
              color="smError"
              data-testid="extensions-not-saved"
            >
              {t(
                "Your changes to the extensions are saved once every length and deadline is valid.",
              )}
            </Text>
          )}

          {rowsAreValid && !noExtensionSaved && length && (
            <Text fontSize="sm" data-testid="extension-total">
              {length.parts.length > (shownExtensions ?? []).length
                ? t(
                    "Up to {{total}} {{unit}} with all extensions: {{parts}} {{unit}}",
                    {
                      total: length.total,
                      parts: length.parts.join(" + "),
                      unit: unitLabel,
                    },
                  )
                : t(
                    "The extensions add up to {{total}} {{unit}}: {{parts}} {{unit}}",
                    {
                      total: length.total,
                      parts: length.parts.join(" + "),
                      unit: unitLabel,
                    },
                  )}
            </Text>
          )}

          {rowsAreValid && options?.renewalDescription && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb="1">
                {t("Text in the notice")}
              </Text>
              <Text
                fontSize="sm"
                color="smTextSecondary"
                bg="smBackgroundSecondary"
                borderRadius="md"
                p="3"
                data-testid="renewal-description"
              >
                {options.renewalDescription}
              </Text>
            </Box>
          )}
        </Stack>
      )}
    </FormControl>
  );
};
